import MuxPlayer from '@mux/mux-player-react';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useEffect, useRef, useState } from 'react';
import { VideoSeekSlider } from 'react-video-seek-slider';
import styled from 'styled-components';

import {
  getMuxHostedThumbnailUrl,
  getSelfHostedThumbnailUrl,
} from '../../utility/story';
import { getThumbnailTime } from '../../utility/videoClip';

import type MuxPlayerElement from '@mux/mux-player';
import type { MuxPlayerElementEventMap } from '@mux/mux-player';
import type { AspectRatio } from '@src/types.ts/video';
import type { CSSProperties } from 'react';

type CSSPropertiesWithVars = CSSProperties & {
  [key: `--${string}`]: string | number;
};

const playerStyle: CSSPropertiesWithVars = {
  maxHeight: '100%',
  width: '100%',
  height: '100%',
  borderRadius: 10,
  overflow: 'hidden',
  border: '1px solid #484848',
  aspectRatio: 4 / 3,
  '--pip-button': 'none',
  '--fullscreen-button': 'none',
  '--seek-live-button': 'none',
  '--seek-backward-button': 'none',
  '--seek-forward-button': 'none',
  '--time-range': 'none',
  '--media-object-position': 'center',
  '--time-display': 'none',
  '--duration-display': 'none',
};

export const getPlayerStyle = (
  isShowingPoster: boolean,
  aspectRatio: AspectRatio,
): CSSPropertiesWithVars => {
  return isShowingPoster
    ? {
        ...playerStyle,
        '--media-object-fit': aspectRatio === '9:16' ? 'contain' : 'cover',
      }
    : playerStyle;
};

type Props = {
  clip: {
    startTime: number;
    duration: number;
    muxPlaybackId: string | undefined;
    src: string | undefined;
    poster: string | undefined;
    autoPlay: boolean;
  };
  onPlay?: () => void;
  isPlaying?: boolean;
  aspectRatio?: AspectRatio;
};

const MuxPlayerClip = ({ clip, isPlaying, onPlay, aspectRatio }: Props) => {
  const videoCreator = useVideoCreatorStore();
  const playerRef = useRef<MuxPlayerElement | null>(null);

  const endTime = clip.startTime + clip.duration;

  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [isShowingPoster, setIsShowingPoster] = useState(true);

  useEffect(() => {
    if (
      !isPlaying &&
      playerRef.current &&
      typeof playerRef.current.pause === 'function'
    ) {
      playerRef.current.pause();
    }
  }, [isPlaying]);

  function onMuxTimeUpdate(event: MuxPlayerElementEventMap['timeupdate']) {
    const videoElement = event.target as HTMLVideoElement;
    const seconds = videoElement.currentTime;
    if (
      seconds > clip.startTime &&
      !playerRef.current?.paused &&
      isShowingPoster
    ) {
      setIsShowingPoster(false);
    }
    setCurrentTimeMs((seconds - clip.startTime) * 1000);
    if (seconds > endTime) {
      playerRef.current!.pause();
      playerRef.current!.currentTime = clip.startTime;
    }
  }

  function onSeekSliderChange(newTimeMs: number) {
    playerRef.current!.currentTime = clip.startTime + newTimeMs / 1000;
  }

  let poster = clip.poster;
  // self-hosted thumbnail if not mux video to match same thumbnail after clip is saved
  // todo replace hardcoded cloudfront string. it shouldn't know anything about cloudfront
  if (!poster && !clip.muxPlaybackId) {
    if (videoCreator.story?.useAws) {
      poster = getSelfHostedThumbnailUrl(
        videoCreator.story.originalVideo,
        getThumbnailTime(clip),
      );
    } else {
      poster = getMuxHostedThumbnailUrl(
        videoCreator.story?.originalVideo.video.muxPlaybackId!,
        getThumbnailTime(clip),
      );
    }
  }

  return (
    <PlayerContainer onClick={e => e.stopPropagation()}>
      <MuxPlayer
        ref={playerRef}
        onTimeUpdate={onMuxTimeUpdate}
        style={getPlayerStyle(
          isShowingPoster,
          aspectRatio || videoCreator.getDefaultAspectRatio(),
        )}
        streamType="on-demand"
        playbackId={clip.muxPlaybackId}
        startTime={clip.startTime}
        src={clip.src}
        poster={poster}
        thumbnailTime={!poster ? getThumbnailTime(clip) : undefined}
        autoPlay={clip.autoPlay}
        onPlay={onPlay}
        volume={1}
      />
      <ProgressBarContainer>
        <VideoSeekSlider
          max={clip.duration * 1000}
          currentTime={currentTimeMs}
          onChange={onSeekSliderChange}
          secondsPrefix="00:"
        />
      </ProgressBarContainer>
    </PlayerContainer>
  );
};

export const PlayerContainer = styled.div`
  position: relative;
  height: 100%;
`;

export const ProgressBarContainer = styled.div`
  position: absolute;
  bottom: 52px;
  width: 100%;
`;

export default MuxPlayerClip;
